import { Component } from '@theme/component';

/**
 * Mobile slider controls for the collection product grid
 * (snippets/product-grid.liquid, section setting `mobile_slider`). The grid
 * itself is a CSS scroll-snap row on mobile; this adds prev/next arrows and one
 * dot per card, kept in sync with scrolling and with cards added by infinite
 * scroll or filtering.
 *
 * @typedef {{ dots: HTMLOListElement, previous: HTMLButtonElement, next: HTMLButtonElement }} Refs
 * @extends {Component<Refs>}
 */
class ProductGridSlider extends Component {
  /** @type {MutationObserver | null} */
  #observer = null;

  /** Re-syncs arrows/dots when the layout changes (e.g. desktop ↔ mobile). */
  #resizeObserver = new ResizeObserver(() => this.#update());

  get #grid() {
    return /** @type {HTMLElement | null} */ (this.parentElement?.querySelector('.product-grid--mobile-slider'));
  }

  get #items() {
    return /** @type {HTMLElement[]} */ ([...(this.#grid?.querySelectorAll(':scope > .product-grid__item') ?? [])]);
  }

  connectedCallback() {
    super.connectedCallback();
    const grid = this.#grid;
    if (!grid) return;

    grid.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#resizeObserver.observe(grid);
    this.#observer = new MutationObserver(() => this.#build());
    this.#observer.observe(grid, { childList: true });
    this.#build();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#grid?.removeEventListener('scroll', this.#onScroll);
    this.#observer?.disconnect();
    this.#resizeObserver.disconnect();
  }

  previous() {
    this.select(this.#current() - 1);
  }

  next() {
    this.select(this.#current() + 1);
  }

  /** @param {number} index */
  select(index) {
    const grid = this.#grid;
    const items = this.#items;
    const item = items[Math.max(0, Math.min(index, items.length - 1))];
    if (!grid || !item) return;
    const offset = item.getBoundingClientRect().left - grid.getBoundingClientRect().left - this.#padding(grid);
    grid.scrollTo({ left: grid.scrollLeft + offset, behavior: 'smooth' });
  }

  /** @param {HTMLElement} grid */
  #padding(grid) {
    return parseFloat(getComputedStyle(grid).scrollPaddingInlineStart) || 0;
  }

  // Scroll events already fire at most once per frame; a handful of rect reads is cheap.
  #onScroll = () => this.#update();

  /** Index of the card nearest the scroll start. */
  #current() {
    const grid = this.#grid;
    const items = this.#items;
    if (!grid || !items.length) return 0;
    // At the end of the row the last cards can't snap to the start; treat as last.
    if (grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 2) return items.length - 1;
    const start = grid.getBoundingClientRect().left + this.#padding(grid);
    let best = 0;
    let bestDistance = Infinity;
    items.forEach((item, index) => {
      const distance = Math.abs(item.getBoundingClientRect().left - start);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  #build() {
    const dots = this.refs.dots;
    const count = this.#items.length;
    this.hidden = count < 2;
    if (!dots) return;

    const template = this.dataset.dotLabel || '';
    dots.replaceChildren(
      ...Array.from({ length: count }, (_, index) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-grid-slider__dot';
        button.setAttribute('on:click', `/select/${index}`);
        button.setAttribute(
          'aria-label',
          template.replace('[index]', String(index + 1)).replace('[length]', String(count))
        );
        item.append(button);
        return item;
      })
    );
    this.#update();
  }

  #update() {
    const current = this.#current();
    const count = this.#items.length;
    this.refs.dots?.querySelectorAll('button').forEach((dot, index) => {
      dot.setAttribute('aria-selected', String(index === current));
    });
    if (this.refs.previous) this.refs.previous.disabled = current <= 0;
    if (this.refs.next) this.refs.next.disabled = current >= count - 1;
  }
}

if (!customElements.get('product-grid-slider')) {
  customElements.define('product-grid-slider', ProductGridSlider);
}
